import React, { useState, useRef } from 'react';
import TextField from '../../../shared/components/forms/TextField';
import LexicalRichTextareaEditor from '../../forms/components/LexicalRichTextareaEditor';

interface Education {
  id?: number;
  schoolname?: string;
  qualification?: string;
  start_date?: string;
  end_date?: string;
  description?: string;
  current?: boolean;
  _destroy?: boolean;
}

interface Props {
  education?: Education[];
  onEducationChange?: (education: Education[]) => void;
}

const ProfileEducation: React.FC<Props> = ({
  education = [],
  onEducationChange
}) => {
  const [educations, setEducations] = useState<Education[]>(education);
  const [openAccordions, setOpenAccordions] = useState<{ [key: number]: boolean }>({});
  const newEducationRef = useRef<HTMLDivElement>(null);

  const handleEducationChange = (index: number, field: keyof Education, value: any) => {
    const updatedEducations = educations.map((edu, i) =>
      i === index ? { ...edu, [field]: value } : edu
    );
    setEducations(updatedEducations);
    onEducationChange?.(updatedEducations);
  };

  const addNewEducation = () => {
    const newEducation: Education = {
      schoolname: '',
      qualification: '',
      start_date: '',
      end_date: '',
      description: '',
      current: false
    };

    const updatedEducations = [...educations, newEducation];
    setEducations(updatedEducations);
    onEducationChange?.(updatedEducations);

    const newIndex = updatedEducations.length - 1;
    setOpenAccordions(prev => ({ ...prev, [newIndex]: true }));

    setTimeout(() => {
      newEducationRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const deleteEducation = (index: number) => {
    if (window.confirm('Are you sure you want to delete this education?')) {
      const updatedEducations = educations.filter((_, i) => i !== index);
      setEducations(updatedEducations);
      onEducationChange?.(updatedEducations);

      // Update accordion states
      const newOpenAccordions: { [key: number]: boolean } = {};
      Object.keys(openAccordions).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex < index) {
          newOpenAccordions[keyIndex] = openAccordions[keyIndex];
        } else if (keyIndex > index) {
          newOpenAccordions[keyIndex - 1] = openAccordions[keyIndex];
        }
      });
      setOpenAccordions(newOpenAccordions);
    }
  };

  const moveEducationUp = (index: number) => {
    if (index === 0) return;

    const updatedEducations = [...educations];
    [updatedEducations[index - 1], updatedEducations[index]] =
      [updatedEducations[index], updatedEducations[index - 1]];

    setEducations(updatedEducations);
    onEducationChange?.(updatedEducations);

    // Update accordion states
    const newOpenAccordions = { ...openAccordions };
    const tempState = newOpenAccordions[index - 1];
    newOpenAccordions[index - 1] = newOpenAccordions[index];
    newOpenAccordions[index] = tempState;
    setOpenAccordions(newOpenAccordions);
  };

  const moveEducationDown = (index: number) => {
    if (index === educations.length - 1) return;

    const updatedEducations = [...educations];
    [updatedEducations[index], updatedEducations[index + 1]] =
      [updatedEducations[index + 1], updatedEducations[index]];

    setEducations(updatedEducations);
    onEducationChange?.(updatedEducations);

    // Update accordion states
    const newOpenAccordions = { ...openAccordions };
    const tempState = newOpenAccordions[index];
    newOpenAccordions[index] = newOpenAccordions[index + 1];
    newOpenAccordions[index + 1] = tempState;
    setOpenAccordions(newOpenAccordions);
  };

  const toggleAccordion = (index: number) => {
    setOpenAccordions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getEducationTitle = (education: Education) => {
    if (education.qualification && education.schoolname) {
      return `${education.qualification} at ${education.schoolname}`;
    } else if (education.qualification) {
      return education.qualification;
    } else if (education.schoolname) {
      return education.schoolname;
    }
    return 'New Education';
  };

  return (
    <div className="profile-education">
      <div className="form-section">
        <h3 className="form-section-title">Education</h3>

        {educations.map((education, index) => (
          <div
            key={index}
            className="card mb-3"
            ref={index === educations.length - 1 ? newEducationRef : null}
          >
            <div className="card-header" id={`heading-education-${index}`}>
              <h5 className="mb-0">
                <button
                  className="btn btn-link"
                  type="button"
                  onClick={() => toggleAccordion(index)}
                  aria-expanded={openAccordions[index] || false}
                  aria-controls={`collapse-education-${index}`}
                >
                  {getEducationTitle(education)}
                  <i className={`fas ${openAccordions[index] ? 'fa-chevron-up' : 'fa-chevron-down'} float-right`}></i>
                </button>
              </h5>
              <div className="card-header-actions">
                {index > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => moveEducationUp(index)}
                    title="Move up"
                  >
                    <i className="fas fa-arrow-up"></i>
                  </button>
                )}
                {index < educations.length - 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => moveEducationDown(index)}
                    title="Move down"
                  >
                    <i className="fas fa-arrow-down"></i>
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteEducation(index)}
                  title="Delete"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>

            {openAccordions[index] && (
              <div className="card-body">
                <div className="form-group">
                  <label>School Name</label>
                  <TextField
                    name={`profile[education[${index}]school][schoolname]`}
                    id={`profile_education_school_name_${index}`}
                    value={education.schoolname || ''}
                    onChange={(value) => handleEducationChange(index, 'schoolname', value)}
                    placeholder="e.g., University of California"
                  />
                </div>

                <div className="form-group">
                  <label>Qualification</label>
                  <TextField
                    name={`profile[education[${index}]school][qualification]`}
                    id={`profile_education_school_qualification_${index}`}
                    value={education.qualification || ''}
                    onChange={(value) => handleEducationChange(index, 'qualification', value)}
                    placeholder="e.g., Bachelor of Science in Dental Hygiene"
                  />
                </div>

                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name={`profile[education[${index}]school][start_date]`}
                    id={`profile_education_school_start_date_${index}`}
                    value={education.start_date || ''}
                    onChange={(e) => handleEducationChange(index, 'start_date', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name={`profile[education[${index}]school][end_date]`}
                    id={`profile_education_school_end_date_${index}`}
                    value={education.end_date || ''}
                    onChange={(e) => handleEducationChange(index, 'end_date', e.target.value)}
                    disabled={education.current}
                  />
                </div>

                <div className="form-group checkbox-wrapper">
                  <label htmlFor={`profile_education_school_current_${index}`} className="checkbox-label">
                    <input
                      type="checkbox"
                      id={`profile_education_school_current_${index}`}
                      name={`profile[education[${index}]school][current]`}
                      checked={education.current || false}
                      onChange={(e) => {
                        handleEducationChange(index, 'current', e.target.checked);
                        if (e.target.checked) {
                          handleEducationChange(index, 'end_date', '');
                        }
                      }}
                    />
                    <span>Currently studying here</span>
                  </label>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <LexicalRichTextareaEditor
                    editorState={education.description || ''}
                    onChangeDescription={(value) => handleEducationChange(index, 'description', value)}
                    placeholder="Describe your education, relevant coursework, achievements..."
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={addNewEducation}
        >
          <i className="fas fa-plus"></i> Add Education
        </button>
      </div>
    </div>
  );
};

export default ProfileEducation;
